"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ClipboardPen, Search, Sparkles, ArrowRight } from 'lucide-react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  number: "01",
  title: "Schreib uns.",
  description: "Kurze Nachricht, kurzer Anruf. Wir finden einen Termin.",
  icon: ClipboardPen
}, {
  number: "02",
  title: "Komm vorbei.",
  description: "Schau dir alles an. Setz dich hin. Fang an bei uns zu arbeiten.",
  icon: Search
}, {
  number: "03",
  title: "Bleib.",
  description: "Hat es gepasst? Dann bleib doch einfach für immer bei uns. Fertig.",
  icon: Sparkles
}];
export default function ProcessTimeline() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="79011" id="in-drei-schritten-loslegen" className="px-3 sm:px-4 ![padding-top:144px] ![padding-bottom:144px]">
  <div className="mx-auto w-full bg-[#141414] rounded-[1rem] py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Top: Header - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20 md:mb-28">
        <div>
          <span className="text-sm font-semibold uppercase tracking-widest text-white/40 mb-4 block">So funktioniert's</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
            <span className="text-white">Drei Schritte.</span><br />
            <span className="text-white/40">Kein Papierkram.</span>
          </h2>
        </div>
        <div className="flex items-end">
          <p className="text-lg text-white/50 leading-relaxed">
            Ganz entspannt zum neuen Lieblingsplatz. Such dir einen Tag aus, komm vorbei und fang einfach an zu arbeiten.
          </p>
        </div>
      </div>

      {/* Bottom: Steps + Button */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 items-end">
        {/* Left: Button */}
        <div>
          <Button asChild size="lg" className="h-14 rounded-full bg-[#FF6F61] text-base font-semibold text-white shadow-lg transition-all hover:bg-[#FF6F61]/90 hover:shadow-xl hover:-translate-y-0.5 gap-3" style={{
              paddingLeft: '80px',
              paddingRight: '80px'
            }}>
            <Link to="/kontakt">
              Kontakt aufnehmen
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>

        {/* Right: Steps */}
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {steps.map((step, i) => <div key={i} data-index={i} className="group h-full rounded-2xl bg-white/[0.05] border border-white/10 p-8 transition-all duration-300 hover:bg-white/10 cursor-default">
              <span className="block text-[64px] md:text-[80px] font-black leading-none tracking-tighter text-[#FF6F61]/20 mb-6 transition-colors duration-300 group-hover:text-[#FF6F61]/40">
                {step.number}
              </span>
              <h3 className="text-lg font-bold text-white tracking-tight mb-3">
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed text-white/50">
                {step.description}
              </p>
            </div>)}
        </div>
      </div>
    </div>
  </div>
</section>;
}